const { getElement } = window.BAO.Utils

class HotspotProductCard extends window.BAO.CustomElement() {
  constructor () {
    super()
    this.initialValue = this.labelValue
  }

  setupListeners () {
    super.setupListeners()
    this.listeners.add(this, 'click', this.handleTrigger.bind(this))
    this.listeners.add(this, 'focusin', this.handleTrigger.bind(this))
  }

  handleTrigger (event) {
    const target = event?.target
    const currentTriggerEl = target.closest(this.els?.mediaTrigger?.selector)
    if (!currentTriggerEl) return

    this.setAttribute(
      'data-visible-media-index',
      currentTriggerEl.dataset?.mediaIndex,
    )
    this.els.mediaTrigger.elements.forEach(mediaTriggerEl =>
      mediaTriggerEl.setAttribute(
        this.currentAttribute,
        (mediaTriggerEl === currentTriggerEl).toString(),
      ),
    )
  }

  get currentAttribute () {
    return `aria-current`
  }
}

if (!customElements.get('hotspot-product-card')) {
  customElements.define('hotspot-product-card', HotspotProductCard)
}

class HotspotProductCardQuickBuy extends window.BAO.ProductForm {
  constructor () {
    super()
    this.initialValue = this.labelValue
  }

  connectedCallback () {
    if (super.connectedCallback) super.connectedCallback()

    // Debug element structure
    if (this.form) {
      Array.from(this.form.attributes).map(attr => `${attr.name}="${attr.value}"`).join(' ')
    }
  }

  setupListeners () {
    super.setupListeners()

    // IMPORTANT: Fixed the AND logic to OR logic
    if (!this.selectEl || !this.buttonEl) {
      return
    }

    if (
      this.buttonEl.disabled &&
      this.buttonEl.dataset.enabledShort !== undefined
    ) {
      const upsellButtonHtml = `
        <span class="prd-UpsellCard_ButtonTextLong">
          ${this.buttonEl.dataset.disabled}
        </span>
        <span class="prd-UpsellCard_ButtonTextShort">
          ${this.buttonEl.dataset.enabledShort}
        </span>
      `
      this.buttonEl.innerHTML = upsellButtonHtml
    } else if (this.buttonEl.disabled) {
      this.buttonEl.innerHTML = this.buttonEl.dataset.disabled
    }

    if (this.selectEl) {
      this.listeners.add(
        this.selectEl,
        'change',
        this.handleSelectChange.bind(this),
      )
    }

    if (!this.cartCheck) {
      this.listeners.add(this.form, 'submit', this.onSubmitHandler.bind(this))
    }
  }

  checkValidity () {
    try {
      super.checkValidity()
    } catch (error) {
      console.error('🔍 DEBUG: Error in super.checkValidity():', error)
    }

    if (!this.form) {
      console.error('🔍 DEBUG: Form not found in checkValidity')
      this.throwError(`needs to be wrapped around a product's <form> element`)
    }
  }

  async onSubmitHandler (e) {
    e.preventDefault()

    if (this.form.classList.contains('prd-Card_QuickBuy-bis')) {
      let redirectUrl = `/products/${this.form.dataset.productHandle}`
      if (this.buttonEl.dataset.bisVariantId !== '') {
        redirectUrl += `?variant=${this.buttonEl.dataset.bisVariantId}`
      }

      window.location.href = `${window.location.origin}${redirectUrl}`
    } else {
      try {
        const result = await this.addToCart()
      } catch (error) {
        console.error('🔍 DEBUG: Error in addToCart:', error)
      }
    }
  }

  async addToCart () {
    // Check if window.BAO.Cart exists and has the expected method
    if (!window.BAO.Cart) {
      console.error('🔍 DEBUG: window.BAO.Cart is not defined')
      return Promise.reject(new Error('Cart API missing'))
    }

    if (!window.BAO.Cart.addItemFromForm) {
      console.error('🔍 DEBUG: window.BAO.Cart.addItemFromForm is not a function')
      return Promise.reject(new Error('Cart API method missing'))
    }

    // Alternative approach in case addItemFromForm doesn't work
    try {
      const result = await window.BAO.Cart.addItemFromForm(this.form)
      return result
    } catch (error) {
      console.error('🔍 DEBUG: Error in window.BAO.Cart.addItemFromForm:', error)

      try {
        const formData = new FormData(this.form)
        const id = Number(formData.get('id'))
        const quantity = Number(formData.get('quantity') || 1)

        if (!id) {
          throw new Error('Invalid product ID')
        }

        if (window.BAO.Cart.addItem) {
          return window.BAO.Cart.addItem(
            { id, quantity },
            { form: this.form },
          )
        } else {
          throw new Error('No fallback cart method available')
        }
      } catch (fallbackError) {
        console.error('🔍 DEBUG: Fallback cart addition failed:', fallbackError)
        throw fallbackError
      }
    }
  }

  handleSelectChange (event) {
    const selectEl = event?.target
    if (!selectEl) {
      console.warn('🔍 DEBUG: No select element in event target')
      return
    }

    const value = selectEl.value
    const disabled = !(value && value.length)

    this.buttonEl.disabled = disabled

    if (undefined !== selectEl[selectEl.selectedIndex]?.dataset?.bis) {
      this.buttonEl.innerHTML = this.buttonEl.dataset.bis
      this.buttonEl.dataset.bisVariantId = value
      this.form.classList.add('prd-Card_QuickBuy-bis')
    } else {
      if (this.buttonEl.dataset.enabledShort !== undefined) {
        const upsellButtonHtml = `
          <span class="prd-UpsellCard_ButtonTextLong">
            ${this.buttonEl.dataset.enabled}
          </span>
          <span class="prd-UpsellCard_ButtonTextShort">
            ${this.buttonEl.dataset.enabledShort}
          </span>
        `
        this.buttonEl.innerHTML = upsellButtonHtml
      } else {
        this.buttonEl.innerHTML = this.buttonEl.dataset.enabled
      }

      this.form.classList.remove('prd-Card_QuickBuy-bis')
    }

    const optionEl = getElement(`[value="${value}"]`, { context: selectEl })

    if (!optionEl) return

    this.labelValue = optionEl.textContent
  }

  set labelValue (value) {
    if (!this.labelEl) return

    this.labelEl.textContent =
      value && value.length > 0 ? value : this.initialValue
  }

  get labelValue () {
    const value = this.labelEl?.textContent || ''
    return value
  }

  get selectEl () {
    const el = this.els?.select?.element
    // Don't log every time to reduce console noise
    return el
  }

  get labelEl () {
    const el = this.els?.label?.element
    // Don't log every time to reduce console noise
    return el
  }

  get buttonEl () {
    const el = this.els?.button?.element
    // Don't log every time to reduce console noise
    return el
  }

  /**
   * @returns {HTMLFormElement|null}
   */
  get form () {
    const form = this.querySelector('form')
    // Don't log every time to reduce console noise
    return form
  }

  get cartCheck () {
    const check = this.dataset.cartQuickbuy || false
    // Don't log every time to reduce console noise
    return check
  }
}

if (!customElements.get('hotspot-product-quickbuy')) {
  customElements.define('hotspot-product-quickbuy', HotspotProductCardQuickBuy)
}
